import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  CircularProgress,
  Avatar,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Header from "../../components/Header";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import { motion } from "framer-motion";

import "./TeamStyles.css";

const Team = () => {
  // Initialize hooks and theme
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  // Get authentication context with owner-specific API
  const { user, ownerApi, token, logout } = useAuth();

  // Component state
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4 },
    },
  };

  // DataGrid column definitions
  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.5,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography color={colors.grey[100]} className="cell-with-hover">
            {params.value.substring(0, 8)}...
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          className="driver-name-cell"
        >
          <Avatar
            src={params.row.image || "rd.png"}
            className="driver-avatar"
          />
          <Typography color={colors.greenAccent[300]}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "age",
      headerName: "Age",
      type: "number",
      headerAlign: "left",
      align: "left",
      flex: 0.5,
      renderCell: (params) => (
        <Typography color={colors.grey[100]}>{params.value}</Typography>
      ),
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 1,
      renderCell: (params) => (
        <Typography color={colors.grey[100]}>{params.value}</Typography>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography color={colors.grey[100]} className="cell-with-hover">
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "trips",
      headerName: "No. of Trips",
      type: "number",
      flex: 0.5,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          className="trips-cell"
        >
          <Typography color={colors.grey[100]}>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            className="actions-container"
            display="flex"
            justifyContent="space-around"
            width="100%"
          >
            <Tooltip title="View Details">
              <IconButton
                className="action-button view-button"
                onClick={() => handleViewDriver(params.row.id)}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Edit Driver">
              <IconButton
                className="action-button edit-button"
                onClick={() => handleEditDriver(params.row.id)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Remove Driver">
              <IconButton
                className="action-button delete-button"
                onClick={() => handleRemoveDriver(params.row.id)}
                disabled={deleteLoading && selectedDriverId === params.row.id}
              >
                {deleteLoading && selectedDriverId === params.row.id ? (
                  <CircularProgress size={24} color="error" />
                ) : (
                  <DeleteOutlineIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // Function to handle driver view
  const handleViewDriver = (driverId) => {
    navigate(`/driver-details/${driverId}`);
  };

  // Function to handle driver edit
  const handleEditDriver = (driverId) => {
    navigate(`/driver/${driverId}/edit`);
  };

  // Function to handle driver removal
  const handleRemoveDriver = async (driverId) => {
    if (!window.confirm("Are you sure you want to remove this driver?")) {
      return;
    }

    try {
      setDeleteLoading(true);
      setSelectedDriverId(driverId);
      await ownerApi.delete(`/removeDriver/${user._id}/${driverId}`);

      // Visual feedback for deletion
      setDrivers((prevDrivers) =>
        prevDrivers.filter((driver) => driver.id !== driverId)
      );
      setFilteredDrivers((prevDrivers) =>
        prevDrivers.filter((driver) => driver.id !== driverId)
      );
    } catch (error) {
      console.error("Error removing driver:", error);
      setError(error.response?.data?.message || "Failed to remove driver");
    } finally {
      setDeleteLoading(false);
      setSelectedDriverId(null);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value) {
      const lowercasedSearch = value.toLowerCase();
      const filtered = drivers.filter(
        (driver) =>
          driver.name.toLowerCase().includes(lowercasedSearch) ||
          driver.email.toLowerCase().includes(lowercasedSearch) ||
          driver.phone.includes(value)
      );
      setFilteredDrivers(filtered);
    } else {
      setFilteredDrivers(drivers);
    }
  };

  // Function to fetch drivers data
  const fetchDrivers = async () => {
    if (!user?._id) return;

    try {
      setLoading(true);
      console.log("driver-hit", user._id);
      // Make API request using the authenticated api instance
      const response = await ownerApi.get(`/${user._id}/drivers`);
      console.log("driver-hit22", response);
      if (!response.data?.drivers) {
        setDrivers([]);
        setFilteredDrivers([]);
        return;
      }

      // Transform the API response data to match DataGrid requirements
      const formattedDrivers = response.data.drivers.map((driver) => ({
        id: driver.id || driver._id,
        name: driver.name,
        image: "rd.png",
        age: driver.age || "N/A",
        phone: driver.phone || "N/A",
        email: driver.email || "N/A",
        trips: driver.trips || 0,
      }));

      setDrivers(formattedDrivers);
      setFilteredDrivers(formattedDrivers);
      setError(null);
    } catch (err) {
      console.error("Error fetching drivers:", err);
      // Handle different types of errors
      if (err.response?.status === 401) {
        setError("Session expired. Please login again.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else {
        setError(
          err.response?.data?.message ||
            "Failed to load drivers. Please try again later."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Effect to check authentication and fetch data
  useEffect(() => {
    // Check if user is authenticated
    if (!token) {
      navigate("/login");
      return;
    }

    // Fetch drivers data when component mounts
    fetchDrivers();
  }, [user?._id, ownerApi, token, navigate, refreshKey]);

  // Navigation handler for adding new driver
  const handleAddDriver = () => {
    navigate("/driver");
  };

  // Refresh handler
  const handleRefresh = () => {
    setRefreshKey((prevKey) => prevKey + 1);
  };

  // Loading state
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="loading-container"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" className="loading-text">
          Loading drivers...
        </Typography>
      </motion.div>
    );
  }

  // Render component
  return (
    <div className="team-container">
      {/* Header Section */}
      <div className="team-header">
        <Header title="DRIVERS" subtitle="Managing Your Drivers" />
        <Button
          onClick={handleAddDriver}
          variant="contained"
          className="add-driver-button"
          sx={{
            backgroundColor: colors.blueAccent[700],
            "&:hover": {
              backgroundColor: colors.blueAccent[800],
            },
          }}
        >
          <PersonAddIcon sx={{ mr: "10px" }} />
          Add New Driver
        </Button>
      </div>

      {/* Search and Tools Section */}
      <div className="search-section">
        <TextField
          variant="outlined"
          placeholder="Search drivers..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="search-field"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: colors.primary[400],
              borderRadius: "8px",
              "& fieldset": {
                borderColor: colors.grey[800],
              },
              "&:hover fieldset": {
                borderColor: colors.greenAccent[400],
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.greenAccent[500],
              },
            },
          }}
        />
        <Tooltip title="Refresh data">
          <IconButton onClick={handleRefresh} className="refresh-button">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </div>

      {/* Stats Cards */}
      <div className="stats-section">
        <Box className="stat-card">
          <Typography variant="h6">Total Drivers</Typography>
          <Typography variant="h3">{drivers.length}</Typography>
        </Box>
        <Box className="stat-card">
          <Typography variant="h6">Total Trips</Typography>
          <Typography variant="h3">
            {drivers.reduce((sum, driver) => sum + driver.trips, 0)}
          </Typography>
        </Box>
      </div>

      {/* Main Content */}
      <div className="datagrid-container">
        {error ? (
          // Error State
          <Box className="error-container">
            <Typography color="error" variant="h5" className="error-message">
              {error}
            </Typography>
            {error.includes("session") && (
              <Button
                onClick={() => navigate("/login")}
                variant="contained"
                color="primary"
                className="login-return-button"
              >
                Return to Login
              </Button>
            )}
          </Box>
        ) : filteredDrivers.length === 0 ? (
          // Empty State
          <Box className="empty-state-container">
            <Typography
              variant="h5"
              color={colors.grey[100]}
              className="empty-state-title"
            >
              {searchTerm
                ? "No drivers match your search"
                : "No drivers registered yet"}
            </Typography>
            <Typography
              color={colors.grey[300]}
              className="empty-state-subtitle"
            >
              {searchTerm
                ? "Try a different search term"
                : "Start by adding your first driver"}
            </Typography>
            {!searchTerm && (
              <Button
                onClick={handleAddDriver}
                variant="contained"
                className="empty-add-button"
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  "&:hover": {
                    backgroundColor: colors.blueAccent[800],
                  },
                }}
              >
                <PersonAddIcon sx={{ mr: 1 }} />
                Add Driver
              </Button>
            )}
          </Box>
        ) : (
          // Data Grid - Define fixed height and width
          <Box sx={{ height: "100%", width: "100%" }}>
            <DataGrid
              rows={filteredDrivers}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 20]}
              checkboxSelection
              disableSelectionOnClick
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              components={{
                Toolbar: GridToolbar,
                NoRowsOverlay: () => (
                  <Box className="no-rows-overlay">
                    <Typography color={colors.grey[100]}>
                      No drivers found
                    </Typography>
                  </Box>
                ),
              }}
              sx={{
                height: "100%",
                width: "100%",
                "& .MuiDataGrid-main": { overflow: "hidden" },
                "& .MuiDataGrid-virtualScroller": { overflow: "auto" },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: colors.primary[500],
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                },
              }}
              className="data-grid"
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default Team;
